import React from 'react';
import { motion } from 'framer-motion';
import Box from '@mui/material/Box';
import { useTheme, Theme } from '@mui/material/styles'; // Import useTheme and Theme

// Default images if none are provided
const defaultImages: string[] = [
  'https://placehold.co/250x150/E2E8F0/4A5568?text=Mobile+View+1',
  'https://placehold.co/250x150/CBD5E0/2D3748?text=Mobile+View+2',
  'https://placehold.co/250x150/A0AEC0/1A202C?text=Mobile+View+3',
  'https://placehold.co/250x150/718096/F7FAFC?text=Mobile+View+4',
];

/**
 * Props for the HorizontalImageCarousel component.
 */
interface HorizontalImageCarouselProps {
  /** Array of image URLs. Defaults to placeholder images. */
  images?: string[];
  /** Width of each individual image in pixels. Defaults to 200. */
  imageWidth?: number;
  /** Height of each individual image in pixels. Defaults to 120. */
  imageHeight?: number;
  /** Duration of one full scroll cycle in seconds. Defaults to 25. */
  scrollDuration?: number;
  /** Spacing between images in pixels. Defaults to 16px (theme.spacing(2)). */
  gap?: number;
}

/**
 * HorizontalImageCarousel component (MUI Styled)
 * Displays a continuously scrolling horizontal list of images.
 * Uses MUI's sx prop for styling.
 */
const HorizontalImageCarousel: React.FC<HorizontalImageCarouselProps> = ({
  images = defaultImages,
  imageWidth = 200,
  imageHeight = 120,
  scrollDuration = 25,
  gap = 16, // Default gap in pixels
}) => {
  const theme: Theme = useTheme(); // Access the MUI theme

  // Duplicate images for a seamless looping effect
  const extendedImages: string[] = [...images, ...images];

  // Calculate the total width of one set of images plus gaps.
  const singleSetWidth: number = images.length * imageWidth;
  const totalScrollDistance: number = singleSetWidth + (images.length * gap);

  return (
    // Container for the carousel, defines the viewport.
    <Box
      sx={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        // Using a general borderRadius for the main container, can be theme.shape.borderRadius or specific
        // This was already theme.shape.borderRadius, which is good.
        borderRadius: theme.shape.borderRadius, 
      }}
    >
      {/* motion.div is not an MUI component, so we wrap it or style its children with MUI if needed.
          Here, we'll apply flex container styles directly to the motion.div via its style prop,
          or rely on MUI Box for its direct children if preferred.
          For simplicity with framer-motion, direct style or sx on Box children is often cleaner.
      */}
      <motion.div
        style={{ // Direct style for framer-motion component
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'nowrap',
          height: '100%',
          alignItems: 'center',
        }}
        animate={{
          x: [0, -totalScrollDistance], // Animate the x-coordinate for horizontal scroll
        }}
        transition={{
          x: {
            duration: scrollDuration,
            ease: 'linear', // Constant speed
            repeat: Infinity, // Loop indefinitely
            repeatType: 'loop', // Resets to start for continuous loop
          },
        }}
      >
        {extendedImages.map((src: string, index: number) => (
          <Box
            key={index}
            sx={{
              flexShrink: 0,
              width: `${imageWidth}px`,
              height: `${imageHeight}px`,
              borderRadius: theme.shape.borderRadius, // Rounded corners for the image container
              overflow: 'hidden', // Crucial for ensuring the image inside conforms to the parent's borderRadius
              boxShadow: theme.shadows[2], // Use a subtle shadow from the theme
              // Apply the gap using margin.
              ml: index === 0 ? 0 : `${gap}px`, 
            }}
          >
            <Box
              component="img"
              src={src}
              alt={`Carousel image ${index + 1}`}
              sx={{
                height: '100%',
                width: '100%',
                objectFit: 'cover', // Ensures image fills its container, cropping if necessary
                // Apply theme's border radius directly to the image.
                // Since the parent Box also has borderRadius and overflow:hidden,
                // this ensures the image content itself is rounded.
                borderRadius: theme.shape.borderRadius, 
              }}
              onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                const target = e.target as HTMLImageElement;
                target.onerror = null; // Prevent infinite loop if placeholder also fails
                target.src="https://placehold.co/200x120/FF0000/FFFFFF?text=Load+Error";
              }}
            />
          </Box>
        ))}
      </motion.div>
    </Box>
  );
};

export default HorizontalImageCarousel;
